import { cn } from '@/utils';
import { isMobile } from '@/utils/mobile.util';
import { forwardRef, useCallback, useEffect, useMemo, useRef } from 'react';

import { isNotNilOrEmpty } from 'ramda-adjunct';

import { TextareaProps } from './text-area-form.type';

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
	(props: TextareaProps, ref) => {
		const {
			boxStyle,
			containerClassName,
			maxHeight,
			className,
			// singleLine = true,
			onChange,
			regex,
			defaultHeight,
			currency,
			value,
			maxLength,
			...rest
		} = props;

		const textAreaRef = useRef<HTMLTextAreaElement>(null);

		const formatCurrency = (value: string) => {
			if (!value) return value;
			const digitsOnly = value.replace(/,/g, '');
			if (/\D/.test(digitsOnly)) {
				return value;
			}
			return digitsOnly.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
		};

		const truncateValue = (value: string) => {
			if (maxLength && value) {
				return value.slice(0, maxLength);
			}
			return value;
		};

		const displayValue = useMemo(() => {
			if (currency && value) {
				return truncateValue(formatCurrency(value.toString())) || '';
			} else {
				return value === null || value === undefined
					? ''
					: truncateValue(String(value));
			}
		}, [truncateValue, formatCurrency, value, currency]);

		useEffect(() => {
			if (textAreaRef.current && isNotNilOrEmpty(textAreaRef.current)) {
				textAreaRef.current.style.height = '0';
				textAreaRef.current.style.height =
					(defaultHeight ||
						Math.min(textAreaRef.current.scrollHeight, maxHeight)) + 'px';
			}
		}, [textAreaRef.current, maxHeight, displayValue, defaultHeight]);

		const onClickFocus = useCallback(() => {
			textAreaRef.current?.focus();
		}, [textAreaRef.current]);

		const handleKeyPress = useCallback(
			(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
				if (e.key === 'Enter' && maxHeight < 30) {
					e.preventDefault();
				}
			},
			[maxHeight]
		);

		const _onChange = useCallback(
			(e: React.ChangeEvent<HTMLTextAreaElement>) => {
				let newValue = e.target.value;

				if (regex) {
					newValue = newValue.replace(regex, '');
				}

				if (currency) {
					newValue = formatCurrency(newValue);
				}

				e.target.value = newValue;
				onChange?.(e);
			},
			[regex, onChange, currency]
		);

		return (
			<div
				className={cn('flex items-center cursor-text', containerClassName)}
				style={{
					height: maxHeight ? maxHeight : isMobile ? '11.111vw' : '40px',
					...boxStyle,
				}}
				onClick={onClickFocus}
			>
				<textarea
					ref={ref ? ref : textAreaRef}
					className={cn(
						'w-full text-[12px] text-left text-font2 resize-none overflow-hidden outline-none bg-transparent min-h-[5.556vw] xl:min-h-[20px] font-light',
						className
					)}
					onKeyDown={handleKeyPress}
					onChange={_onChange}
					value={displayValue || ''}
					maxLength={maxLength}
					{...rest}
				/>
			</div>
		);
	}
);

Textarea.displayName = 'Textarea';

export default Textarea;
